/**
 * @module resolvePostPattern
 *
 * Utilitários puros para a estrutura de URLs dos posts (WordPress-style permalinks).
 * Sem dependências de servidor — pode ser importado em Client Components.
 *
 * Estruturas disponíveis:
 *   postname          /%postname%            → /meu-artigo
 *   prefix_postname   /prefixo/%postname%    → /posts/meu-artigo  (padrão)
 *   category_postname /%category%/%postname% → /categoria/meu-artigo
 */

export const POST_URL_STRUCTURES = {
  /** /%postname% — URL curta, apenas o slug do post. */
  POSTNAME: 'postname',
  /** /prefixo/%postname% — prefixo configurável + slug (comportamento padrão). */
  PREFIX_POSTNAME: 'prefix_postname',
  /** /%category%/%postname% — slug da categoria principal + slug do post. */
  CATEGORY_POSTNAME: 'category_postname',
} as const

export type PostUrlStructure = (typeof POST_URL_STRUCTURES)[keyof typeof POST_URL_STRUCTURES]

/** Estrutura usada como fallback quando nenhuma está configurada no CMS. */
export const DEFAULT_POST_URL_STRUCTURE: PostUrlStructure = POST_URL_STRUCTURES.PREFIX_POSTNAME

/**
 * Constrói o caminho público de um post individual com base na estrutura configurada.
 *
 * @param structure    - Estrutura de URL seleccionada no CMS.
 * @param slug         - Slug do post.
 * @param prefix       - Prefixo configurado (relevante para PREFIX_POSTNAME).
 * @param categorySlug - Slug da categoria principal (relevante para CATEGORY_POSTNAME).
 */
export function buildPostUrl(
  structure: PostUrlStructure | null | undefined,
  slug: string,
  prefix: string,
  categorySlug?: string | null,
): string {
  switch (structure) {
    case POST_URL_STRUCTURES.POSTNAME:
      return `/${slug}`
    case POST_URL_STRUCTURES.CATEGORY_POSTNAME:
      return `/${categorySlug || 'sem-categoria'}/${slug}`
    case POST_URL_STRUCTURES.PREFIX_POSTNAME:
    default:
      return `/${prefix}/${slug}`
  }
}

/**
 * Extrai o slug da categoria usada no permalink (1.ª com `slug` preenchido).
 * Compatível com Post do Payload (categorias populadas ou só ID — neste caso devolve null).
 */
export function extractPrimaryCategorySlug(categories: unknown): string | null {
  if (!Array.isArray(categories) || !categories.length) return null
  for (const item of categories) {
    if (typeof item === 'object' && item !== null && 'slug' in item) {
      const slug = (item as { slug?: string | null }).slug?.trim()
      if (slug) return slug
    }
  }
  return null
}
